import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  Alert,
  Button,
  Chip,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { SparePageHeader, SpareSummary, sparePageSx } from "../../components/SparePagePresentation";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import BuildIcon from "@mui/icons-material/Build";
import EngineeringIcon from "@mui/icons-material/Engineering";
import RefreshIcon from "@mui/icons-material/Refresh";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

import { useAuth } from "../../../../contexts/AuthContext";
import {
  AppSearchField,
  AppToolbar,
} from "../../../../components/common";

import AssignTechnicianDialog from "../components/AssignTechnicianDialog";
import CloseTicketDialog, { SpareClosureLine } from "../components/CloseTicketDialog";
import ServiceTicketDetailsDialog from "../components/ServiceTicketDetailsDialog";
import ServiceTicketFormDialog from "../components/ServiceTicketFormDialog";
import ServiceTicketTable from "../components/ServiceTicketTable";
import SpareIssueDialog from "../components/SpareIssueDialog";
import SpareReturnDialog, {
  SpareReturnForm,
  SpareReturnLine,
} from "../components/SpareReturnDialog";
import TicketTimeline from "../components/TicketTimeline";
import PreventiveMaintenancePage from "../../preventive-maintenance/pages/PreventiveMaintenancePage";
import maintenanceService, { mapTicket } from "../api/maintenance.service";
import { useMaintenance } from "../hooks/useMaintenance";
import {
  AssignTechnicianPayload,
  CloseTicketPayload,
  HubOption,
  ServiceType,
  ServiceTicket,
  ServiceTicketDetails,
  ServiceTicketPayload,
  SpareIssueOption,
  Technician,
  TicketTimeline as TicketTimelineItem,
  VehicleOption,
} from "../types";
import { filterRowsBySearch } from "../../utils/rowSearch";

const maintenanceTabs: Array<{
  value: ServiceType;
  title: string;
  subtitle: string;
  cta: string;
  accent: string;
}> = [
  {
    value: "ON_ROAD",
    title: "On-Road Service",
    subtitle: "Rider app damage tickets, AME assignment, technician repair, spare issue, and invoice closure.",
    cta: "Raise On-Road Ticket",
    accent: "#e91e63",
  },
  {
    value: "IN_HOUSE",
    title: "In-House Maintenance",
    subtitle: "Workshop maintenance for returned fleet vehicles before redeployment.",
    cta: "Raise Workshop Ticket",
    accent: "#ef7d00",
  },
  {
    value: "PREVENTIVE",
    title: "Preventive Schedules",
    subtitle: "Deployed vehicles and their 45-day preventive service schedule.",
    cta: "Schedule Maintenance",
    accent: "#d97706",
  },
];

const MaintenancePage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const linkedTicketId = searchParams.get("ticketId");
  const [linkedTicketError, setLinkedTicketError] = useState<string | null>(null);
  const { user } = useAuth();
  const {
    serviceTickets,
    analytics,
    loading,
    pagination,
    filters,
    updateFilters,
    refresh,
    createServiceTicket,
    updateServiceTicket,
    assignTechnician,
    updateTicketStatus,
    closeTicket,
    deleteServiceTicket,
  } = useMaintenance();

  const [openTicketDialog, setOpenTicketDialog] = useState(false);
  const [openAssignDialog, setOpenAssignDialog] = useState(false);
  const [openCloseDialog, setOpenCloseDialog] = useState(false);
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  const [openSpareIssueDialog, setOpenSpareIssueDialog] = useState(false);
  const [openSpareReturnDialog, setOpenSpareReturnDialog] = useState(false);
  const [openTimeline, setOpenTimeline] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<ServiceTicket | null>(null);
  const [technicians, setTechnicians] = useState<Technician[]>([]);
  const [ames, setAmes] = useState<Technician[]>([]);
  const [inspectors, setInspectors] = useState<Technician[]>([]);
  const [inspectorsLoading, setInspectorsLoading] = useState(false);
  const [hubs, setHubs] = useState<HubOption[]>([]);
  const [vehicles, setVehicles] = useState<VehicleOption[]>([]);
  const [availableSpares, setAvailableSpares] = useState<SpareIssueOption[]>([]);
  const [returnItems, setReturnItems] = useState<SpareReturnLine[]>([]);
  const [closeSpareCost, setCloseSpareCost] = useState(0);
  const [closeSpareSummary, setCloseSpareSummary] = useState<SpareClosureLine[]>([]);
  const [ticketDetails, setTicketDetails] = useState<ServiceTicketDetails | null>(null);
  const [timeline, setTimeline] = useState<TicketTimelineItem[]>([]);
  const [vehiclesLoading, setVehiclesLoading] = useState(false);
  const [sparesLoading, setSparesLoading] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [timelineLoading, setTimelineLoading] = useState(false);
  useEffect(() => {
    if (!linkedTicketId) return;
    let cancelled = false;
    setLinkedTicketError(null);
    setDetailsLoading(true);
    maintenanceService.getServiceTicketDetails(linkedTicketId)
      .then((details) => {
        if (cancelled) return;
        setSelectedTicket(mapTicket(details));
        setTicketDetails(details);
        setOpenDetailsDialog(true);
      })
      .catch((error) => {
        if (!cancelled) setLinkedTicketError(error?.response?.data?.message || "Unable to open the linked ticket.");
      })
      .finally(() => { if (!cancelled) setDetailsLoading(false); });
    return () => { cancelled = true; };
  }, [linkedTicketId]);
  const activeServiceType = filters.serviceType || "ON_ROAD";
  const activeTab = maintenanceTabs.find((tab) => tab.value === activeServiceType) || maintenanceTabs[0];
  const currentEmployeeKeys = useMemo(
    () =>
      new Set(
        [user?.id, user?.userId, user?.employeeId, user?.email]
          .filter(Boolean)
          .map(String)
      ),
    [user]
  );
  const currentActorId = useMemo(
    () =>
      String(
        (user as any)?.employeeRecordId ||
          user?.employeeId ||
          user?.id ||
          ""
      ),
    [user]
  );

  useEffect(() => {
    maintenanceService
      .getTechnicians()
      .then(setTechnicians)
      .catch(() => setTechnicians([]));

    maintenanceService
      .getAmes()
      .then(setAmes)
      .catch(() => setAmes([]));

    maintenanceService
      .getHubs()
      .then(setHubs)
      .catch(() => setHubs([]));

    maintenanceService
      .getVehicles()
      .then(setVehicles)
      .catch(() => setVehicles([]));
  }, []);

  const mergeVehicles = useCallback((nextVehicles: VehicleOption[]) => {
    setVehicles((currentVehicles) => {
      const vehicleById = new Map(
        currentVehicles.map((vehicle) => [vehicle.id, vehicle])
      );

      nextVehicles.forEach((vehicle) => {
        vehicleById.set(vehicle.id, vehicle);
      });

      return Array.from(vehicleById.values());
    });
  }, []);

  const fetchVehiclesForHub = useCallback(
    async (hubId: string) => {
      if (!hubId) return;

      try {
        setVehiclesLoading(true);
        mergeVehicles(await maintenanceService.getVehicles({ hubId }));
      } catch {
        mergeVehicles([]);
      } finally {
        setVehiclesLoading(false);
      }
    },
    [mergeVehicles]
  );

  const fetchInspectorsForHub = useCallback(
    async (hubId: string) => {
      const cityId = hubs.find((hub) => hub.id === hubId)?.cityId;

      if (!cityId) {
        setInspectors([]);
        return;
      }

      try {
        setInspectorsLoading(true);
        const [cityAmes, cityTechnicians] = await Promise.all([
          maintenanceService.getAmes({ cityId }),
          maintenanceService.getTechnicians({ cityId }),
        ]);

        const inspectorsById = new Map<string, Technician>();
        cityAmes.forEach((employee) => {
          inspectorsById.set(employee.id, {
            ...employee,
            name: `${employee.name} - AME`,
          });
        });
        cityTechnicians.forEach((employee) => {
          inspectorsById.set(employee.id, {
            ...employee,
            name: `${employee.name} - Technician`,
          });
        });
        setInspectors(Array.from(inspectorsById.values()));
      } catch {
        setInspectors([]);
      } finally {
        setInspectorsLoading(false);
      }
    },
    [hubs]
  );

  useEffect(() => {
    if (!openAssignDialog || !selectedTicket) return;

    const cityId = hubs.find((hub) => hub.id === selectedTicket.warehouseId)?.cityId;
    maintenanceService
      .getTechnicians(cityId ? { cityId } : undefined)
      .then(setTechnicians)
      .catch(() => setTechnicians([]));
  }, [hubs, openAssignDialog, selectedTicket]);

  const technicianDisplayById = useMemo(() => {
    const entries = technicians.flatMap((technician) => {
      const display = technician.name || technician.employeeId || technician.id;

      return [
        [technician.id, display],
        ...(technician.employeeId ? [[technician.employeeId, display] as const] : []),
      ] as Array<readonly [string, string]>;
    });

    return new Map(entries);
  }, [technicians]);

  const employeeDisplayById = useMemo(() => {
    const entries = [...technicians, ...ames].flatMap((employee) => {
      const display = employee.name || employee.employeeId || employee.id;
      return [
        [employee.id, display],
        ...(employee.employeeId ? [[employee.employeeId, display] as const] : []),
        ...(employee.email ? [[employee.email, display] as const] : []),
      ] as Array<readonly [string, string]>;
    });

    return new Map(entries);
  }, [ames, technicians]);

  const isCurrentAme = useMemo(
    () =>
      ames.some((ame) =>
        [ame.id, ame.employeeId, ame.email]
          .filter(Boolean)
          .map(String)
          .some((key) => currentEmployeeKeys.has(key))
      ),
    [ames, currentEmployeeKeys]
  );

  const isCurrentTechnician = useMemo(
    () =>
      technicians.some((technician) =>
        [technician.id, technician.employeeId, technician.email]
          .filter(Boolean)
          .map(String)
          .some((key) => currentEmployeeKeys.has(key))
      ),
    [currentEmployeeKeys, technicians]
  );

  const maintenanceAccessRole =
    isCurrentAme || !isCurrentTechnician ? "MANAGER" : "TECHNICIAN";

  const displayTickets = useMemo(
    () =>
      serviceTickets.map((ticket) => ({
        ...ticket,
        vehicleNumber:
          vehicles.find(
            (vehicle) =>
              vehicle.id === ticket.vehicleId ||
              vehicle.registrationNumber === ticket.vehicleId
          )?.registrationNumber ||
          ticket.vehicleNumber ||
          ticket.vehicleId,
        chassisNumber:
          vehicles.find(
            (vehicle) =>
              vehicle.id === ticket.vehicleId ||
              vehicle.registrationNumber === ticket.vehicleId
          )?.chassisNumber ||
          ticket.chassisNumber,
        assignedTechnicianName:
          (ticket.assignedTechnicianId &&
            technicianDisplayById.get(ticket.assignedTechnicianId)) ||
          ticket.assignedTechnicianName ||
          "-",
      })),
    [serviceTickets, technicianDisplayById, vehicles]
  );

  const ticketStats = useMemo(() => {
    const openStatuses = new Set([
      "RAISED",
      "ASSIGNED",
      "IN_PROGRESS",
      "SPARE_ISSUED",
      "WAITING_FOR_PARTS",
      "RETURN_PENDING",
      "RETURN_VERIFIED",
    ]);

    return displayTickets.reduce(
      (stats, ticket) => {
        const createdDate = new Date(ticket.createdAt);
        const ageInDays = Math.floor(
          (Date.now() - createdDate.getTime()) / (1000 * 60 * 60 * 24)
        );

        return {
          open: stats.open + (openStatuses.has(ticket.status) ? 1 : 0),
          scheduled: stats.scheduled + (ticket.status === "RAISED" ? 1 : 0),
          inspection: stats.inspection + (ticket.status === "RAISED" ? 1 : 0),
          assigned: stats.assigned + (ticket.status === "ASSIGNED" ? 1 : 0),
          inProgress:
            stats.inProgress +
            (["IN_PROGRESS", "SPARE_ISSUED", "WAITING_FOR_PARTS"].includes(
              ticket.status
            )
              ? 1
              : 0),
          qcPending: stats.qcPending + (ticket.status === "COMPLETED" ? 1 : 0),
          readyForDeployment:
            stats.readyForDeployment +
            (ticket.status === "INVOICE_GENERATED" ? 1 : 0),
          completed:
            stats.completed +
            (["COMPLETED", "CLOSED"].includes(ticket.status) ? 1 : 0),
          overdue:
            stats.overdue +
            (ticket.status !== "CLOSED" && ageInDays > 45 ? 1 : 0),
          critical: stats.critical + (ticket.priority === "CRITICAL" ? 1 : 0),
        };
      },
      {
        open: 0,
        scheduled: 0,
        inspection: 0,
        assigned: 0,
        inProgress: 0,
        qcPending: 0,
        readyForDeployment: 0,
        completed: 0,
        overdue: 0,
        critical: 0,
      }
    );
  }, [displayTickets]);

  const cards = useMemo(
    () => {
      if (activeServiceType === "PREVENTIVE") {
        return [
          {
            title: "Scheduled",
            value: ticketStats.scheduled,
            icon: <AssignmentTurnedInIcon />,
            color: "#4f46e5",
          },
          {
            title: "Assigned",
            value: ticketStats.assigned,
            icon: <EngineeringIcon />,
            color: "#0288d1",
          },
          {
            title: "In Progress",
            value: ticketStats.inProgress,
            icon: <BuildIcon />,
            color: "#f59e0b",
          },
          {
            title: "Completed",
            value: ticketStats.completed,
            icon: <AssignmentTurnedInIcon />,
            color: "#2e7d32",
          },
          {
            title: "Overdue",
            value: ticketStats.overdue,
            icon: <WarningAmberIcon />,
            color: "#d32f2f",
          },
        ];
      }

      if (activeServiceType === "IN_HOUSE") {
        return [
          {
            title: "Inspection",
            value: ticketStats.inspection,
            icon: <WarningAmberIcon />,
            color: "#ef7d00",
          },
          {
            title: "Repairing",
            value: ticketStats.inProgress,
            icon: <BuildIcon />,
            color: "#f59e0b",
          },
          {
            title: "QC Pending",
            value: ticketStats.qcPending,
            icon: <AssignmentTurnedInIcon />,
            color: "#7c3aed",
          },
          {
            title: "Ready For Deployment",
            value: ticketStats.readyForDeployment,
            icon: <AssignmentTurnedInIcon />,
            color: "#0288d1",
          },
          {
            title: "Completed",
            value: displayTickets.filter((ticket) => ticket.status === "CLOSED").length,
            icon: <AssignmentTurnedInIcon />,
            color: "#2e7d32",
          },
        ];
      }

      return [
        {
          title: "Open Tickets",
          value: ticketStats.open,
          icon: <BuildIcon />,
          color: "#1976d2",
        },
        {
          title: "Assigned",
          value: ticketStats.assigned,
          icon: <EngineeringIcon />,
          color: "#0288d1",
        },
        {
          title: "Completed",
          value: ticketStats.completed,
          icon: <AssignmentTurnedInIcon />,
          color: "#2e7d32",
        },
        {
          title: "Critical",
          value: ticketStats.critical,
          icon: <WarningAmberIcon />,
          color: "#d32f2f",
        },
      ];
    },
    [activeServiceType, displayTickets, ticketStats]
  );

  const handleSubmitTicket = async (payload: ServiceTicketPayload) => {
    if (selectedTicket) {
      await updateServiceTicket(selectedTicket.id, payload);
    } else {
      await createServiceTicket(payload);
    }

    setOpenTicketDialog(false);
    setSelectedTicket(null);
    await refresh();
  };

  const handleAssign = async (payload: AssignTechnicianPayload) => {
    if (!selectedTicket) return;

    if (selectedTicket.status !== "RAISED") {
      window.alert("Technician can be assigned only at the first workflow step.");
      return;
    }

    try {
      await assignTechnician(selectedTicket.id, payload);
      setOpenAssignDialog(false);
      setSelectedTicket(null);
      await refresh();
    } catch (error: any) {
      console.error("Unable to assign technician", error);
      window.alert(
        error?.response?.data?.message ||
          "Unable to assign the technician. Please try again."
      );
    }
  };

  const handleCloseTicket = async (_payload: CloseTicketPayload) => {
    if (!selectedTicket) return;

    if (["IN_PROGRESS", "SPARE_ISSUED", "WAITING_FOR_PARTS"].includes(selectedTicket.status)) {
      await updateTicketStatus(selectedTicket.id, "COMPLETED", _payload);
      const completedTicket = {
        ...selectedTicket,
        status: "COMPLETED" as const,
      };

      setOpenCloseDialog(false);
      setCloseSpareSummary([]);
      await refresh();
      await handleOpenSpareReturn(completedTicket);
      return;
    } else if (["COMPLETED", "RETURN_VERIFIED", "INVOICE_GENERATED"].includes(selectedTicket.status)) {
      await closeTicket(selectedTicket.id, _payload);
    } else {
      window.alert(
        "Ticket can be completed only after work starts, and closed only after completion or verified spare return."
      );
      return;
    }

    setOpenCloseDialog(false);
    setSelectedTicket(null);
    setCloseSpareSummary([]);
    await refresh();
  };

  const handleStartWork = async (ticket: ServiceTicket) => {
    if (ticket.status !== "ASSIGNED") {
      window.alert("Assign a technician before starting work.");
      return;
    }

    await updateTicketStatus(ticket.id, "IN_PROGRESS");
    await refresh();
  };

  const handleReadyForDeployment = async (ticket: ServiceTicket) => {
    if (ticket.status !== "COMPLETED") {
      window.alert("Quality check must be completed before marking ready.");
      return;
    }

    await updateTicketStatus(ticket.id, "INVOICE_GENERATED");
    await refresh();
  };

  const handleOpenSpareIssue = async (ticket: ServiceTicket) => {
    setSelectedTicket(ticket);
    setOpenSpareIssueDialog(true);

    try {
      setSparesLoading(true);
      setAvailableSpares(await maintenanceService.getAvailableSpares(ticket.warehouseId));
    } catch {
      setAvailableSpares([]);
    } finally {
      setSparesLoading(false);
    }
  };

  const handleIssueSpares = async (payload: {
    remarks?: string;
    items: Array<{
      sparePartId: string;
      quantity: number;
      remarks?: string;
    }>;
  }) => {
    if (!selectedTicket) return;

    const technicianId = selectedTicket.assignedTechnicianId;
    if (!technicianId || technicianId === "-") {
      window.alert("Please assign a technician before issuing spares.");
      return;
    }

    let availableAmes = ames;

    if (maintenanceAccessRole === "MANAGER" && !availableAmes.length) {
      try {
        availableAmes = await maintenanceService.getAmes();
        setAmes(availableAmes);
      } catch {
        availableAmes = [];
      }
    }

    const issuingAme = availableAmes.find((ame) =>
      [ame.id, ame.employeeId, ame.email]
        .filter(Boolean)
        .map(String)
        .some((key) => currentEmployeeKeys.has(key))
    );
    const issuedById =
      maintenanceAccessRole === "MANAGER"
        ? issuingAme?.id || availableAmes[0]?.id
        : undefined;
    if (maintenanceAccessRole === "MANAGER" && !issuedById) {
      window.alert("Please assign at least one AME in Maintenance Assignments before issuing spares.");
      return;
    }

    const requestedByPartId = new Map<string, number>();
    payload.items.forEach((item) => {
      requestedByPartId.set(
        item.sparePartId,
        (requestedByPartId.get(item.sparePartId) || 0) + Number(item.quantity || 0)
      );
    });

    for (const [sparePartId, requestedQuantity] of requestedByPartId) {
      const spare = availableSpares.find((part) => part.id === sparePartId);

      if (!spare) {
        window.alert("Please select a valid spare part.");
        return;
      }

      if (requestedQuantity <= 0) {
        window.alert("Quantity must be greater than zero.");
        return;
      }

      if (requestedQuantity > spare.availableStock) {
        window.alert(
          `${spare.name} has only ${spare.availableStock} units available in this hub.`
        );
        return;
      }

      if (
        spare.maxQuantityPerServiceIssue != null &&
        requestedQuantity > spare.maxQuantityPerServiceIssue
      ) {
        window.alert(
          `${spare.name} has a maximum issue limit of ${spare.maxQuantityPerServiceIssue} units per service ticket.`
        );
        return;
      }
    }

    try {
      setSparesLoading(true);

      await maintenanceService.issueSpares({
        ticketId: selectedTicket.id,
        warehouseId: selectedTicket.warehouseId,
        technicianId,
        ...(issuedById ? { issuedById } : {}),
        createdById: currentActorId || technicianId,
        remarks: payload.remarks,
        items: payload.items.map((item) => {
          const spare = availableSpares.find(
            (part) => part.id === item.sparePartId
          );

          return {
            sparePartId: item.sparePartId,
            quantity: Number(item.quantity),
            unitCost: spare?.unitCost || 1,
            remarks: item.remarks,
          };
        }),
      });

      setOpenSpareIssueDialog(false);
      setSelectedTicket(null);
      setAvailableSpares([]);
      await refresh();
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        (maintenanceAccessRole === "MANAGER"
          ? "Unable to issue spare parts. Please check stock, hub, and technician."
          : "Unable to issue spare parts. Please check stock, hub, and technician.");

      window.alert(message);
    } finally {
      setSparesLoading(false);
    }
  };

  const buildReturnItems = (details: ServiceTicketDetails): SpareReturnLine[] => {
    const issuedByPartId = new Map<string, SpareReturnLine>();

    (details.spareIssues || []).forEach((issue) => {
      if (["CANCELLED"].includes(String(issue.status).toUpperCase())) return;

      (issue.items || []).forEach((item) => {
        const current = issuedByPartId.get(item.sparePartId);
        const quantity = Number(item.quantity || 0);
        const sparePartName = item.sparePart?.name || item.sparePartId;
        const partCode = item.sparePart?.code || item.sparePart?.partNumber || "-";

        issuedByPartId.set(item.sparePartId, {
          sparePartId: item.sparePartId,
          sparePartName: current?.sparePartName || sparePartName,
          partCode: current?.partCode || partCode,
          issuedQty: (current?.issuedQty || 0) + quantity,
          usedQty: (current?.usedQty || 0) + quantity,
          returnedQty: 0,
          acceptedQty: 0,
          damagedQty: 0,
          remarks: "",
        });
      });
    });

    (details.spareReturns || []).forEach((spareReturn) => {
      if (["REJECTED", "CANCELLED"].includes(String(spareReturn.status).toUpperCase())) {
        return;
      }

      (spareReturn.items || []).forEach((item) => {
        const current = issuedByPartId.get(item.sparePartId);
        if (!current) return;

        const issuedQty = Math.max(
          0,
          current.issuedQty - Number(item.returnedQty || 0)
        );

        issuedByPartId.set(item.sparePartId, {
          ...current,
          issuedQty,
          usedQty: issuedQty,
        });
      });
    });

    return Array.from(issuedByPartId.values()).filter((item) => item.issuedQty > 0);
  };

  const handleOpenSpareReturn = async (ticket: ServiceTicket) => {
    try {
      setSparesLoading(true);
      const details = await maintenanceService.getServiceTicketDetails(ticket.id);
      const items = buildReturnItems(details);

      if (!items.length) {
        setSelectedTicket(null);
        setReturnItems([]);
        setOpenSpareReturnDialog(false);
        return;
      }

      setSelectedTicket(ticket);
      setReturnItems(items);
      setOpenSpareReturnDialog(true);
    } catch {
      setReturnItems([]);
      setOpenSpareReturnDialog(false);
    } finally {
      setSparesLoading(false);
    }
  };

  const handleOpenCloseTicket = async (ticket: ServiceTicket) => {
    setSelectedTicket(ticket);
    setCloseSpareCost(0);
    setCloseSpareSummary([]);
    setOpenCloseDialog(true);

    try {
      const details = await maintenanceService.getServiceTicketDetails(ticket.id);
      const summaryByPartId = new Map<string, SpareClosureLine>();
      const usagePartIds = new Set<string>();
      const ensureLine = (
        sparePartId: string,
        sparePart?: { name?: string; code?: string; partNumber?: string } | null
      ) => {
        const existing = summaryByPartId.get(sparePartId);

        if (existing) return existing;

        const line: SpareClosureLine = {
          sparePartId,
          name: sparePart?.name || sparePartId,
          code: sparePart?.code || sparePart?.partNumber || "-",
          issuedQty: 0,
          consumedQty: 0,
          returnedQty: 0,
          acceptedQty: 0,
          damagedQty: 0,
        };
        summaryByPartId.set(sparePartId, line);
        return line;
      };

      (details.spareIssues || []).forEach((issue) => {
        if (issue.status === "CANCELLED") return;

        (issue.items || []).forEach((item) => {
          ensureLine(item.sparePartId, item.sparePart).issuedQty += Number(item.quantity || 0);
        });
      });

      (details.spareUsages || []).forEach((usage) => {
        ensureLine(usage.sparePartId, usage.sparePart).consumedQty += Number(usage.quantityUsed || 0);
        usagePartIds.add(usage.sparePartId);
      });

      (details.spareReturns || []).forEach((spareReturn) => {
        if (["REJECTED", "CANCELLED"].includes(spareReturn.status)) return;

        (spareReturn.items || []).forEach((item) => {
          const line = ensureLine(item.sparePartId, item.sparePart);
          line.returnedQty += Number(item.returnedQty || 0);
          line.acceptedQty += Number(item.acceptedQty || 0);
          line.damagedQty += Number(item.damagedQty || 0);

          if (!usagePartIds.has(item.sparePartId)) {
            line.consumedQty += Number(item.usedQty || 0);
          }
        });
      });

      setCloseSpareSummary(Array.from(summaryByPartId.values()));
      const spareCost = (details.spareUsages || []).reduce(
        (total, usage) =>
          total + Number(usage.quantityUsed || 0) * Number(usage.unitCost || 0),
        0
      );
      setCloseSpareCost(spareCost);
    } catch {
      setCloseSpareCost(0);
      setCloseSpareSummary([]);
    }
  };

  const handleReturnSpares = async (payload: SpareReturnForm) => {
    if (!selectedTicket) return;

    const technicianId = selectedTicket.assignedTechnicianId;

    if (!technicianId || technicianId === "-") {
      window.alert("Please assign a technician before returning spares.");
      return;
    }

    const items = payload.items
      .map((item) => ({
        sparePartId: item.sparePartId,
        issuedQty: Number(item.issuedQty || 0),
        usedQty: Number(item.usedQty || 0),
        returnedQty: Number(item.returnedQty || 0),
        remarks: item.remarks,
      }))
      .filter((item) => item.issuedQty > 0);

    if (!items.length) {
      window.alert("Please record used and returned quantities for the issued spares.");
      return;
    }

    try {
      setSparesLoading(true);
      await maintenanceService.returnSpares({
        ticketId: selectedTicket.id,
        warehouseId: selectedTicket.warehouseId,
        technicianId,
        createdById: technicianId,
        remarks: payload.remarks,
        items,
      });

      setOpenSpareReturnDialog(false);
      setSelectedTicket(null);
      setReturnItems([]);
      await refresh();
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "Unable to return spare parts.";

      window.alert(message);
    } finally {
      setSparesLoading(false);
    }
  };

  const handleOpenTimeline = async (ticket: ServiceTicket) => {
    setSelectedTicket(ticket);
    setOpenTimeline(true);

    try {
      setTimelineLoading(true);
      const events = await maintenanceService.getTicketTimeline(ticket.id);
      const displayEmployee = (value: string) =>
        employeeDisplayById.get(value) ||
        (value === "ADMIN" ? "System Administrator" : value);

      setTimeline(
        events.map((event) => {
          let description = event.description || "";
          employeeDisplayById.forEach((name, employeeId) => {
            if (employeeId) {
              description = description.split(employeeId).join(name);
            }
          });

          return {
            ...event,
            createdBy: displayEmployee(event.createdBy),
            description,
          };
        })
      );
    } catch {
      setTimeline([]);
    } finally {
      setTimelineLoading(false);
    }
  };

  const handleOpenDetails = async (ticket: ServiceTicket) => {
    setSelectedTicket(ticket);
    setOpenDetailsDialog(true);

    try {
      setDetailsLoading(true);
      setTicketDetails(await maintenanceService.getServiceTicketDetails(ticket.id));
    } catch {
      setTicketDetails(null);
    } finally {
      setDetailsLoading(false);
    }
  };

  return (
    <Box
      sx={sparePageSx("maintenance")}
    >
      <SparePageHeader kind="maintenance"
        title="Maintenance Management"
        subtitle="On-road, preventive, and in-house ticket control for AME operations."
        action={
          maintenanceAccessRole === "MANAGER" && activeServiceType !== "PREVENTIVE" ? (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setSelectedTicket(null);
              setOpenTicketDialog(true);
            }}
          >
            {activeTab.cta}
          </Button>
          ) : undefined
        }
      />

      {linkedTicketError && <Alert severity="error" sx={{ mb: 2 }}>{linkedTicketError}</Alert>}
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        <Tabs
          value={activeServiceType}
          onChange={(_, serviceType: ServiceType) => {
            const params = new URLSearchParams(searchParams);
            params.set("serviceType", serviceType);
            params.delete("ticketId");
            setSearchParams(params);
            updateFilters({
              serviceType,
              page: 1,
              search: "",
            });
          }}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            px: 2,
            borderBottom: "1px solid",
            borderColor: "divider",
            "& .MuiTab-root": {
              minHeight: 44,
              textTransform: "none",
              fontWeight: 800,
            },
          }}
        >
          {maintenanceTabs.map((tab) => (
            <Tab
              key={tab.value}
              value={tab.value}
              label={tab.title}
              sx={{
                color: activeServiceType === tab.value ? tab.accent : undefined,
              }}
            />
          ))}
        </Tabs>

        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
          spacing={1}
          sx={{ px: 2, py: 1, bgcolor: "action.hover" }}
        >
          <Box>
            <Typography variant="caption" color="text.secondary">
              {activeTab.subtitle}
            </Typography>
          </Box>

          {activeServiceType !== "PREVENTIVE" && (
            <Chip
              label={`${pagination.total} Tickets`}
              sx={{
                fontWeight: 800,
                color: activeTab.accent,
                borderColor: activeTab.accent,
              }}
              variant="outlined"
            />
          )}
        </Stack>
      </Paper>

      {activeServiceType === "PREVENTIVE" ? (
        <PreventiveMaintenancePage embedded />
      ) : (
        <>
      <SpareSummary items={[
        ...cards.map((card) => ({ label: card.title + " · this page", value: loading ? "—" : card.value, icon: card.icon, color: card.color })),
        { label: "Total Tickets", value: loading ? "—" : pagination.total, icon: <AssignmentTurnedInIcon />, color: "#ec4899" },
      ]} />

      <AppToolbar
        left={
          <AppSearchField
            placeholder="Search ticket, vehicle, or issue..."
            value={filters.search ?? ""}
            onChange={(value) =>
              updateFilters({
                search: value,
                page: 1,
              })
            }
          />
        }
        right={
          <Button startIcon={<RefreshIcon />} onClick={() => refresh()}>
            Refresh
          </Button>
        }
      />

      <ServiceTicketTable
        rows={filterRowsBySearch(displayTickets, filters.search)}
        loading={loading}
        page={(filters.page || 1) - 1}
        pageSize={filters.limit || 10}
        rowCount={pagination.total}
        onEdit={(ticket) => {
          setSelectedTicket(ticket);
          setOpenTicketDialog(true);
        }}
        onView={handleOpenDetails}
        onAssign={(ticket) => {
          setSelectedTicket(ticket);
          setOpenAssignDialog(true);
        }}
        onIssueParts={handleOpenSpareIssue}
        onReturnParts={handleOpenSpareReturn}
        onTimeline={handleOpenTimeline}
        onCloseTicket={handleOpenCloseTicket}
        onDelete={(ticket) => deleteServiceTicket(ticket.id)}
        activeServiceType={activeServiceType}
        onStartWork={handleStartWork}
        onReadyForDeployment={handleReadyForDeployment}
        accessRole={maintenanceAccessRole}
        onPageChange={(page) =>
          updateFilters({
            page: page + 1,
          })
        }
        onPageSizeChange={(pageSize) =>
          updateFilters({
            page: 1,
            limit: pageSize,
          })
        }
      />

      <ServiceTicketFormDialog
        open={openTicketDialog}
        loading={loading}
        ticket={selectedTicket}
        hubs={hubs}
        vehicles={vehicles}
        inspectors={inspectors}
        inspectorsLoading={inspectorsLoading}
        vehiclesLoading={vehiclesLoading}
        defaultServiceType={activeServiceType}
        onHubChange={fetchVehiclesForHub}
        onInspectorHubChange={fetchInspectorsForHub}
        onClose={() => {
          setOpenTicketDialog(false);
          setSelectedTicket(null);
        }}
        onSubmit={handleSubmitTicket}
      />

      <ServiceTicketDetailsDialog
        open={openDetailsDialog}
        ticket={selectedTicket}
        details={ticketDetails}
        loading={detailsLoading}
        onClose={() => {
          setOpenDetailsDialog(false);
          setSelectedTicket(null);
          setTicketDetails(null);
        }}
      />

      <AssignTechnicianDialog
        open={openAssignDialog}
        loading={loading}
        ticket={selectedTicket}
        technicians={technicians}
        onClose={() => {
          setOpenAssignDialog(false);
          setSelectedTicket(null);
        }}
        onSubmit={handleAssign}
      />

      <CloseTicketDialog
        open={openCloseDialog}
        loading={loading}
        ticket={selectedTicket}
        onClose={() => {
          setOpenCloseDialog(false);
          setSelectedTicket(null);
          setCloseSpareCost(0);
          setCloseSpareSummary([]);
        }}
        onSubmit={handleCloseTicket}
        spareCost={closeSpareCost}
        spareSummary={closeSpareSummary}
      />

      <SpareIssueDialog
        open={openSpareIssueDialog}
        loading={loading || sparesLoading}
        ticket={selectedTicket}
        spareParts={availableSpares}
        mode="issue"
        hubName={
          selectedTicket
            ? hubs.find((hub) => hub.id === selectedTicket.warehouseId)?.name
            : undefined
        }
        onClose={() => {
          setOpenSpareIssueDialog(false);
          setSelectedTicket(null);
          setAvailableSpares([]);
        }}
        onSubmit={handleIssueSpares}
      />

      <SpareReturnDialog
        open={openSpareReturnDialog}
        loading={loading || sparesLoading}
        ticket={selectedTicket}
        items={returnItems}
        onClose={() => {
          setOpenSpareReturnDialog(false);
          setSelectedTicket(null);
          setReturnItems([]);
        }}
        onSubmit={handleReturnSpares}
      />

      <TicketTimeline
        open={openTimeline}
        ticketNumber={selectedTicket?.ticketNumber}
        timeline={timelineLoading ? [] : timeline}
        onClose={() => {
          setOpenTimeline(false);
          setSelectedTicket(null);
          setTimeline([]);
        }}
      />
        </>
      )}
    </Box>
  );
};

export default MaintenancePage;
